import {
  ArrowRight,
  GraduationCap,
  BookOpen,
  FlaskConical,
  Trophy,
  Cloud,
  Bug,
  ShieldCheck,
  Swords,
  Users,
  School,
  Building2,
  Heart,
  CheckCircle2,
  Sparkles,
  Gift,
  Bot,
  Package,
  KeyRound,
  Globe,
  Code2,
  Terminal,
  Layers,
} from 'lucide-react';
import { TrinetraLoop3 } from '@nirvikailabs/brand/icons';

/**
 * learning.nirvik.ai — education platform landing.
 *
 * Tone: warm, inviting, student-first. Big promise: "security literacy
 * for the next billion builders." Free for learners, free for
 * educators. Community-rooted. Not a bootcamp; not a cert mill — a
 * curriculum that scales because AI tutoring scales.
 *
 * Visual identity: dark-mode-default to match nirvik.ai (the <html>
 * ships with `class="dark"`). India-green leads the accent on the deep
 * indigo surface (growth / knowledge connotation), turmeric anchors the
 * CTAs for brand continuity. Softer rounded shapes + generous whitespace
 * so the page feels like a school, not a dashboard.
 *
 * Key sections:
 *   1. Nav with "Start Learning Free" CTA
 *   2. Hero — warm, students-in-frame
 *   3. Why now — AI changed attackers; it can change defenders too
 *   4. Curriculum tracks (4 tracks, india-green-forward)
 *   5. How learning works (plain-language + interactive + project-based)
 *   6. For Educators — campus onboarding
 *   7. Free-for-everyone CTA
 *   8. Tricolor strip + footer
 *
 * START_URL — where "start learning" lands. For now it points to the
 * existing app's /learn route; later this becomes its own learner portal. */
const START_URL = 'https://learning.nirvik.ai';
const EDUCATOR_EMAIL = 'educators@nirvik.ai';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ─── Announcement strip ───
       * Warmer than Trinetra's banner — this one reads as "free, open"
       * news rather than a product launch. india-green → turmeric
       * gradient to signal education + community. */}
      <a
        href="#tracks"
        className="block bg-gradient-to-r from-emerald-800 via-amber-700 to-red-900 text-white text-center py-2.5 px-4 text-xs sm:text-sm font-medium hover:opacity-95 transition-opacity"
      >
        <span className="inline-flex items-center justify-center gap-2 flex-wrap">
          <Gift className="h-4 w-4 shrink-0" />
          <span>
            <strong>Free for every learner</strong>
            <span className="hidden sm:inline"> — open curriculum, interactive labs, and educator tools</span>
          </span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </span>
      </a>

      {/* ─── Nav ─── */}
      <nav className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 max-w-6xl mx-auto gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <TrinetraLoop3 className="h-7 w-7 sm:h-8 sm:w-8 text-primary-strong shrink-0" />
          <span className="font-display text-xl sm:text-2xl font-semibold tracking-wide text-foreground truncate">
            Nirvik <span className="text-india-green">Learning</span>
          </span>
        </div>
        <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <a href="#tracks" className="hover:text-foreground transition-colors">Tracks</a>
          <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
          <a href="#educators" className="hover:text-foreground transition-colors">For educators</a>
          <a href="https://nirvik.ai" className="hover:text-foreground transition-colors">Nirvik AI Labs</a>
        </div>
        <a
          href={START_URL}
          className="text-sm font-medium bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
        >
          Start Learning
          <span className="hidden sm:inline"> Free</span>
        </a>
      </nav>

      {/* ─── Hero ───
       * Warmer than Nirvik or Trinetra — emphasizes "everyone". Student
       * badge on top, soft india-green halo behind. */}
      <section className="relative px-4 sm:px-6 py-16 md:py-24 max-w-4xl mx-auto text-center overflow-hidden">
        <div className="absolute inset-0 -z-10 flex items-start justify-center pointer-events-none">
          <div className="w-[650px] h-[450px] bg-halo-green opacity-70" />
        </div>

        <div className="inline-flex items-center gap-2 bg-india-green/12 border border-india-green/30 text-india-green px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-6">
          <Heart className="h-4 w-4 shrink-0" />
          Free. Open. For every builder.
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold text-foreground leading-[1.05] mb-6">
          Cyber Security Fluency
          <br />
          <span className="text-india-green">for the next billion builders</span>
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
          An interactive curriculum that teaches cybersecurity and makes it accessible to every kind of builder on the planet.

        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <a
            href={START_URL}
            className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 sm:px-6 py-3 rounded-lg text-base sm:text-lg font-medium hover:opacity-90 transition-opacity shadow-sm"
          >
            Start Learning Free
            <ArrowRight className="h-5 w-5 shrink-0" />
          </a>
          <a
            href="#tracks"
            className="inline-flex items-center justify-center gap-2 bg-secondary text-foreground px-5 sm:px-6 py-3 rounded-lg text-base sm:text-lg font-medium border border-border hover:bg-accent transition-colors"
          >
            Browse tracks
          </a>
        </div>

        <p className="mt-8 text-xs text-muted-foreground inline-flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-india-green" />
          Free forever · Open curriculum · For every builder
        </p>
      </section>

      {/* ─── Why now ───
       * Echoes the Nirvik manifesto but pivots to education: the same AI
       * inflection that makes attackers formidable also makes teaching
       * scale. Keeps the narrative consistent across the three sites. */}
      <section className="px-4 sm:px-6 py-16 md:py-20 bg-muted/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xs font-bold uppercase tracking-[0.24em] text-india-green mb-6 text-center">
            Why now
          </h2>
          <p className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-center text-foreground mb-8 leading-tight">
            The next billion lines of code <span className="text-muted-foreground/70">will be generated by builders
              who have never written a single line of code before.</span>
          </p>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed text-center">
            Security fluency is now a foundational skill for every builder — the practitioner, the student, the vibe coder.
            We're building a curriculum that meets people where they are: bite-sized, hands-on, paired with an AI tutor
            that explains until it clicks. Because the builders generating tomorrow's software
            are the ones who will shape how secure it is.
          </p>
        </div>
      </section>

      {/* ─── Curriculum tracks ─── */}
      <section id="tracks" className="px-4 sm:px-6 py-16 md:py-24 max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Tracks built for how builders build today.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Self-paced, hands-on, and designed for the age of AI-assisted development.
          </p>
        </div>

        {/* Live tracks */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {[
            {
              icon: ShieldCheck,
              title: 'Security Fundamentals for Builders',
              description: 'Authn, authz, TLS, secrets management, and the most common vulnerabilities — practical and hands-on, for builders who are actively shipping code. 14 lessons, 3 labs.',
              length: '6 weeks · Beginner',
              iconBg: 'bg-india-green/15 text-india-green border-india-green/30',
              ring: 'hover:border-india-green/40',
            },
            {
              icon: Bot,
              title: 'Securing AI-Generated Code',
              description: 'For builders using Cursor, Claude Code, and Copilot. Learn to review, test, and harden AI-generated code with confidence before it reaches production.',
              length: '5 weeks · Beginner–Intermediate',
              iconBg: 'bg-primary/15 text-primary-strong border-primary/30',
              ring: 'hover:border-primary/40',
            },
            {
              icon: Cloud,
              title: 'Cloud Defense',
              description: 'Understand and secure IAM, storage, networking, and IaC across AWS, GCP, and Azure. Live-cloud labs in disposable sandboxes. 18 lessons.',
              length: '8 weeks · Intermediate',
              iconBg: 'bg-chakra-navy/15 text-chakra-navy border-chakra-navy/35',
              ring: 'hover:border-chakra-navy/45',
            },
            {
              icon: Swords,
              title: 'AI Red Team',
              description: 'Prompt injection, model extraction, agentic exploit chains — how attackers weaponize frontier models, and how defenders stay ahead. 12 lessons.',
              length: '5 weeks · Advanced',
              iconBg: 'bg-saffron/15 text-saffron border-saffron/35',
              ring: 'hover:border-saffron/45',
            },
          ].map((track) => (
            <div
              key={track.title}
              className={`p-6 sm:p-7 rounded-2xl bg-card border border-border transition-all hover:-translate-y-0.5 ${track.ring}`}
            >
              <div className="flex items-start justify-between mb-5">
                <div className={`h-12 w-12 rounded-xl border flex items-center justify-center ${track.iconBg}`}>
                  <track.icon className="h-6 w-6" />
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                  {track.length}
                </span>
              </div>
              <h3 className="font-display text-lg sm:text-xl font-semibold text-foreground mb-2">{track.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{track.description}</p>
            </div>
          ))}
        </div>

        {/* Coming soon tracks */}
        <div className="mt-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Opening soon</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                icon: Layers,
                title: 'Secure Vibe Coding: Lovable & Bolt',
                description: 'You prompted it into existence. This track walks through securing it end to end — credentials, auth, database access, and the configurations that matter most for apps built with Lovable, Bolt, and similar AI builders.',
              },
              {
                icon: Globe,
                title: 'Replit: Prototype to Secure Deploy',
                description: 'Replit gets you running in seconds. This track covers what to secure before you go live: env secrets, visibility settings, port management, and deploy configurations.',
              },
              {
                icon: Terminal,
                title: 'Claude Code & Cursor: Agentic Dev Security',
                description: 'When your IDE agent writes and runs code autonomously, the security surface changes. Manage secrets in context, review agent output, and build safe agentic workflows.',
              },
              {
                icon: Code2,
                title: 'v0 to Vercel: Securing the Frontend Stack',
                description: 'v0 generates your UI; Vercel ships it. Close the gap: env vars, CORS, API route exposure, and auth patterns for the frontend-first AI stack.',
              },
            ].map((track) => (
              <div
                key={track.title}
                className="p-5 rounded-2xl bg-muted/40 border border-border/50 opacity-70"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="h-10 w-10 rounded-xl border border-border bg-muted flex items-center justify-center">
                    <track.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 bg-muted border border-border/50 px-2 py-1 rounded-full">
                    Coming Soon
                  </span>
                </div>
                <h3 className="font-display text-base font-semibold text-muted-foreground mb-1.5">{track.title}</h3>
                <p className="text-xs text-muted-foreground/70 leading-relaxed">{track.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How learning works ─── */}
      <section id="how" className="px-4 sm:px-6 py-16 md:py-24 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-center text-foreground mb-3">
            How learning works here
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground text-center mb-14 max-w-2xl mx-auto">
            Designed around how humans actually learn — in context, at their own pace, with immediate feedback.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: BookOpen,
                title: 'Plain-language lessons',
                description: 'Every concept explained like a mentor would. 5–12 minutes each. Read, watch, or listen.',
                iconColor: 'text-india-green',
              },
              {
                icon: FlaskConical,
                title: 'Hands-on labs',
                description: 'Work with real tools in live sandboxes. Apply what you learn, see the results firsthand, and build intuition through practice.',
                iconColor: 'text-primary-strong',
              },
              {
                icon: Sparkles,
                title: 'An AI tutor that never gets tired',
                description: 'Ask anything in your own words. The tutor knows the lesson, sees your work, and keeps explaining until it clicks.',
                iconColor: 'text-chakra-navy',
              },
            ].map((item) => (
              <div key={item.title} className="p-6 rounded-xl bg-card border border-border">
                <div className="h-11 w-11 rounded-xl bg-muted flex items-center justify-center mb-4">
                  <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── For Educators ───
       * Secondary audience — schools, bootcamps, universities. The card
       * design is deliberately simpler (single block, not a grid) so it
       * doesn't compete with the learner-facing sections. */}
      <section id="educators" className="px-4 sm:px-6 py-16 md:py-24 max-w-5xl mx-auto">
        <div className="rounded-3xl border border-chakra-navy/25 bg-gradient-to-br from-chakra-navy/5 via-card to-india-green/5 p-8 sm:p-12 md:p-14">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-chakra-navy/10 border border-chakra-navy/25 text-chakra-navy px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-5">
                <School className="h-4 w-4" />
                For educators
              </div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-tight">
                Bring Nirvik Learning to your campus
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
                Free curriculum for every accredited university, community college, and non-profit bootcamp.
                Classroom dashboards, assignment grading, and private cohort spaces — no per-seat pricing, ever.
              </p>
              <ul className="space-y-2.5 mb-6">
                {[
                  'Ready-to-teach lesson plans for semester or bootcamp formats',
                  'Private cohort spaces with instructor-only views',
                  'Automatic grading on hands-on labs, with human-style feedback',
                  'Custom tracks — we help you build what your program needs',
                ].map((bullet) => (
                  <li key={bullet} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4.5 w-4.5 text-india-green mt-0.5 shrink-0" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="shrink-0 md:min-w-[200px]">
              <a
                href={`mailto:${EDUCATOR_EMAIL}`}
                className="inline-flex items-center justify-center gap-2 bg-chakra-navy text-chakra-navy-foreground w-full md:w-auto px-6 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity"
              >
                Contact educators team
                <ArrowRight className="h-5 w-5" />
              </a>
              <p className="text-xs text-muted-foreground mt-3 text-center md:text-right">
                We reply within one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── For Teams ─── */}
      <section id="teams" className="px-4 sm:px-6 py-16 md:py-24 max-w-5xl mx-auto">
        <div className="rounded-3xl border border-saffron/25 bg-gradient-to-br from-saffron/5 via-card to-primary/5 p-8 sm:p-12 md:p-14">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-saffron/10 border border-saffron/25 text-saffron px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-5">
                <Building2 className="h-4 w-4" />
                For teams
              </div>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-4 leading-tight">
                Upskill your team for the age of agentic AI
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
                Focused workshops for engineering and product teams at SMBs and enterprises.
                Build the skills your team needs to work confidently with AI agents, MCP-connected tools,
                and the new generation of AI-assisted development workflows — with security built in from day one.
              </p>
              <ul className="space-y-2.5 mb-6">
                {[
                  'Working with AI agents and multi-agent systems — safely and effectively',
                  'Leveraging MCP to connect AI to your tools, data, and infrastructure',
                  'Securing AI-assisted development workflows across your team',
                  'Custom workshops scoped to your stack, your tools, and your team\'s level',
                ].map((bullet) => (
                  <li key={bullet} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4.5 w-4.5 text-saffron mt-0.5 shrink-0" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-muted-foreground/60 mt-4">
                Teams can choose to pay a nominal fee to support the free-for-everyone mission of Nirvik Learning.
                We love to support teams of all sizes, from startups to enterprises.
              </p>

            </div>

            <div className="shrink-0 md:min-w-[200px]">
              <a
                href="mailto:teams@nirvik.ai"
                className="inline-flex items-center justify-center gap-2 bg-saffron text-background w-full md:w-auto px-6 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity"
              >
                Talk to us
                <ArrowRight className="h-5 w-5" />
              </a>
              <p className="text-xs text-muted-foreground mt-3 text-center md:text-right">
                We reply within one business day.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Community / values ─── */}
      <section className="px-4 sm:px-6 py-16 md:py-20 bg-muted/40">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-6 leading-tight">
            Security should be a public good.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            The internet is built on a shared infrastructure — its safety has to be a shared project too.
            Nirvik Learning will stay free for individuals and schools, forever.
            Funded by the Trinetra platform; stewarded by Nirvik Labs.
          </p>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-10">
            {[
              { icon: Bug, label: '120+ vulnerabilities covered' },
              { icon: Users, label: 'Open to every learner' },
              { icon: GraduationCap, label: 'Partnered with universities' },
            ].map((item) => (
              <div
                key={item.label}
                className="inline-flex items-center gap-2 bg-card border border-border px-4 py-2 rounded-full text-sm text-muted-foreground"
              >
                <item.icon className="h-4 w-4 text-india-green" />
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="px-4 sm:px-6 py-16 md:py-20">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-4">
            Ready to start?
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mb-10">
            Pick a track, or start with a single lesson. Every concept is explained step by step, at your pace.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href={START_URL}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity shadow-sm"
            >
              Start Learning Free
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href={`mailto:${EDUCATOR_EMAIL}`}
              className="inline-flex items-center justify-center gap-2 bg-secondary text-foreground px-7 py-3.5 rounded-xl text-base font-semibold border border-border hover:bg-accent transition-colors"
            >
              I'm an educator
            </a>
          </div>
        </div>
      </section>

      {/* ─── Tricolor strip ─── */}
      <div className="flex">
        <div className="h-px flex-1 bg-saffron" />
        <div className="h-px flex-1 bg-white" />
      </div>

      {/* ─── Footer ─── */}
      <footer className="px-4 sm:px-6 py-8 md:py-10 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <TrinetraLoop3 className="h-5 w-5 text-primary-strong" />
            <span className="font-display text-sm font-semibold tracking-wide text-muted-foreground">
              Nirvik Learning
            </span>
            <span className="text-xs text-muted-foreground/70 ml-2">
              A <a href="https://nirvik.ai" className="underline underline-offset-2 hover:text-foreground transition-colors">Nirvik AI Labs</a> project
            </span>
          </div>
          <p className="text-xs text-muted-foreground text-center sm:text-right">
            &copy; 2026 Nirvik AI Labs. Free for every learner.
          </p>
        </div>
      </footer>
    </div>
  );
}
